import { Button, Flex, Form, Text, TextField } from '@approvalmax/ui/src/components';
import { xeroConstants } from 'modules/common/constants';
import { memo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useSetQuoteDecisionByCustomer } from 'shared/data/webApp/v1';

import { useGetQuoteSearchParams } from '../../hooks/useGetQuoteParams';
import { messages } from './DecisionMakerForm.messages';
import { QuoteDecisionFormData } from './DecisionMakerForm.types';

export const DecisionMakerForm = memo(() => {
    const { code, requestId, companyId } = useGetQuoteSearchParams();

    const { mutate: setQuoteDecisionByCustomer, isLoading: isLoadingSetQuoteDecisionByCustomer } =
        useSetQuoteDecisionByCustomer();

    const form = useForm<QuoteDecisionFormData>();
    const submitForm = useCallback(
        (isAccepted: boolean) => {
            if (!code || !companyId || !requestId) return;

            form.handleSubmit((decisionMakerParams) => {
                setQuoteDecisionByCustomer({
                    body: {
                        code,
                        requestId,
                        companyId,
                        isAccepted,
                        decisionMakerParams,
                    },
                });
            })();
        },
        [code, companyId, form, requestId, setQuoteDecisionByCustomer]
    );

    const acceptForm = useCallback(() => submitForm(true), [submitForm]);
    const rejectForm = useCallback(() => submitForm(false), [submitForm]);

    const validateRequired = useCallback((value?: string) => Boolean(value?.trim()), []);

    return (
        <Form form={form}>
            <Flex direction='column'>
                <Text font='headline' fontSize='xsmall' color='midnight80' as='h1'>
                    {messages.sectionTitle}
                </Text>

                <TextField
                    name='firstName'
                    label={messages.firstNameFieldTitle}
                    placeholder={messages.fieldsPlaceholder}
                    maxLength={xeroConstants.CUSTOMER_FIRST_NAME_MAX_LENGTH}
                    disabled={isLoadingSetQuoteDecisionByCustomer}
                    required
                    validate={validateRequired}
                />

                <TextField
                    name='lastName'
                    label={messages.lastNameFieldTitle}
                    placeholder={messages.fieldsPlaceholder}
                    maxLength={xeroConstants.CUSTOMER_LAST_NAME_MAX_LENGTH}
                    disabled={isLoadingSetQuoteDecisionByCustomer}
                    required
                    validate={validateRequired}
                />

                <TextField
                    name='title'
                    label={messages.titleFieldTitle}
                    placeholder={messages.fieldsPlaceholder}
                    maxLength={xeroConstants.CUSTOMER_JOB_TITLE_MAX_LENGTH}
                    disabled={isLoadingSetQuoteDecisionByCustomer}
                    required
                    validate={validateRequired}
                />

                <TextField
                    name='company'
                    label={messages.companyFieldTitle}
                    placeholder={messages.fieldsPlaceholder}
                    maxLength={xeroConstants.CUSTOMER_COMPANY_NAME_MAX_LENGTH}
                    disabled={isLoadingSetQuoteDecisionByCustomer}
                    required
                    validate={validateRequired}
                />

                <TextField
                    name='comment'
                    label={messages.commentFieldTitle}
                    placeholder={messages.fieldsPlaceholder}
                    maxLength={xeroConstants.CUSTOMER_COMMENT_MAX_LENGTH}
                    disabled={isLoadingSetQuoteDecisionByCustomer}
                    multiline
                    rows={6}
                />

                <Flex container wrap={false} spacing='8'>
                    <Flex size={10}>
                        <Button
                            onClick={rejectForm}
                            color='red40'
                            block
                            disabled={!form.formState.isValid}
                            progress={isLoadingSetQuoteDecisionByCustomer}
                        >
                            {messages.rejectButtonText}
                        </Button>
                    </Flex>

                    <Flex size={14}>
                        <Button
                            onClick={acceptForm}
                            color='mint80'
                            block
                            disabled={!form.formState.isValid}
                            progress={isLoadingSetQuoteDecisionByCustomer}
                        >
                            {messages.approveButtonText}
                        </Button>
                    </Flex>
                </Flex>
            </Flex>
        </Form>
    );
});

DecisionMakerForm.displayName = 'DecisionMakerForm';
